/**
 * How long the table is allowed to finish speaking before a screen covers it.
 *
 * Its own module, and not a pair of numbers inside `@/main`, for one reason:
 * the right answer is *derived* from how long the effects actually last, and a
 * derivation that nothing checks is a comment. Here it can be tested against
 * the values it depends on, so shortening a popup cannot silently start
 * truncating the celebration that popup exists to deliver.
 */

import { VARIANTS } from '@/vfx/scorePopup';

/**
 * Beat after the last tracked animation, for a round that ended on a play.
 *
 * The movement is already covered — the caller waits for the sequences to drain
 * before this starts running down. This is the pause afterwards, so the numbers
 * the play produced can be read rather than merely glimpsed.
 */
export const HOLD_AFTER_PLAY = 1.1;

/**
 * Beat for a cleared deck, which is the best thing that can happen here.
 *
 * A perfect game fires a `grand` popup and its own particles from the
 * `game:over` event itself. Those are fired and forgotten — no sequence tracks
 * them — so unless this covers the popup's whole life, the scoreboard lands on
 * top of the celebration it is scoring.
 */
export const HOLD_AFTER_PERFECT = VARIANTS.grand.life + 0.2;

/**
 * Beat for a round that ended with nothing moving — almost always the clock.
 *
 * There is nothing to wait for, and a full pause on a still table does not read
 * as a moment of respect. It reads as a hang.
 */
export const HOLD_IDLE = 0.35;

/**
 * How long to hold the table before showing the breakdown.
 *
 * @param perfectGame - the player cleared the whole deck.
 * @param busy - something was still animating when the round ended.
 */
export function gameOverHold(perfectGame: boolean, busy: boolean): number {
  if (perfectGame) return HOLD_AFTER_PERFECT;
  return busy ? HOLD_AFTER_PLAY : HOLD_IDLE;
}
